import { reportApi } from "#/api/reportApi";
import { Skeleton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReportFooter from "./components/ReportFooter";
import ItemsBySiteChart from "./components/ItemsBySiteChart";
import KeyFindings from "./components/KeyFindings";
import NeverScannedSection from "./components/NeverScannedSection";
import OverdueSection from "./components/OverdueSection";
import ReportHeader from "./components/ReportHeader";
import ScanAgeDistribution from "./components/ScanAgeDistribution";
import ScanAgeOverview from "./components/ScanAgeOverview";
import SiteSummaryTable from "./components/SiteSummaryTable";
import StatusOverview from "./components/StatusOverview";
import ShipmentManifestsSection from "./components/ShipmentManifestsSection";
import StatusFilterDropdown from "./components/StatusFilterDropdown";
import TimeOnSiteSection from "./components/TimeOnSiteSection";
import RequiredStockLevelSection from "./components/RequiredStockLevelSection";
import ImmobilizedStockSection from "./components/ImmobilizedStockSection";
import ClientsSection from "./components/ClientsSection";
import useReportTooltip from "./hooks/useReportTooltip";
import { exportReportToPdf } from "./utils/exportPdf";

const ReportsPage = () => {
  const { t } = useTranslation();
  const reportRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const { data: report, isFetching } = useQuery({
    queryKey: ["inventory-scan-report"],
    queryFn: async () => {
      const response = await reportApi.getInventoryScanReport();
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  // Status filter shared by the top cards and the Status Breakdown section.
  const statusEntries = useMemo(() => report?.statusBreakdown ?? [], [report]);
  const allStatuses = useMemo(() => statusEntries.map((entry) => entry.status), [statusEntries]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  // Default to all statuses; keep the selection valid as the report loads/refetches.
  useEffect(() => {
    setSelectedStatuses((prev) => {
      const valid = prev.filter((status) => allStatuses.includes(status));
      return valid.length > 0 ? valid : allStatuses;
    });
  }, [allStatuses]);

  const excludesFieldItems = report?.overdueExcludesFieldItems ?? false;
  const overdueExtra = useMemo(
    () => (excludesFieldItems ? [t("reports.inventoryScan.overdue.excludesFieldItems")] : undefined),
    [excludesFieldItems, t],
  );

  const tooltips = {
    totalItems: useReportTooltip("totalItems"),
    nonFieldItems: useReportTooltip("nonFieldItems"),
    issuedToField: useReportTooltip("issuedToField"),
    neverScanned: useReportTooltip("neverScanned"),
    scanAge: useReportTooltip("scanAge"),
    scanAgeDistribution: useReportTooltip("scanAgeDistribution"),
    statusBreakdown: useReportTooltip("statusBreakdown"),
    itemsBySite: useReportTooltip("itemsBySite"),
    siteSummary: useReportTooltip("siteSummary"),
    overdue0to7: useReportTooltip("overdue0to7"),
    overdue8to30: useReportTooltip("overdue8to30", overdueExtra),
    overdue31to90: useReportTooltip("overdue31to90", overdueExtra),
    overdue90plus: useReportTooltip("overdue90plus", overdueExtra),
    neverScannedSection: useReportTooltip("neverScanned"),
    timeOnSite: useReportTooltip("timeOnSite"),
    requiredStockLevel: useReportTooltip("requiredStockLevel"),
    immobilizedStock: useReportTooltip("immobilizedStock"),
    clients: useReportTooltip("clients"),
    keyFindings: useReportTooltip("keyFindings"),
  };

  const handleExportPdf = useCallback(async () => {
    if (!reportRef.current || isExporting) return;

    setIsExporting(true);
    try {
      await exportReportToPdf(reportRef.current);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting]);

  if (isFetching || !report) {
    return (
      <div className="flex w-full flex-col gap-6">
        <Skeleton height={60} radius="md" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={120} radius="xl" />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Skeleton height={250} radius="xl" />
          <Skeleton height={250} radius="xl" />
        </div>
        <Skeleton height={300} radius="xl" />
        <Skeleton height={300} radius="xl" />
      </div>
    );
  }

  return (
    <div ref={reportRef} className="flex w-full flex-col gap-6">
      <ReportHeader
        generatedAt={report.generatedAt}
        totalItems={report.generalStatistics.totalItems}
        onExportPdf={handleExportPdf}
        isExporting={isExporting}
      />

      <div className="flex flex-col gap-5">
        <StatusOverview
          total={{
            count: report.generalStatistics.totalItems,
            value: report.generalStatistics.totalEstimatedValue,
          }}
          data={report.statusBreakdown}
          selected={selectedStatuses}
          totalTooltip={tooltips.totalItems}
          tooltip={tooltips.statusBreakdown}
          rightSection={
            allStatuses.length > 0 ? (
              <StatusFilterDropdown
                statuses={allStatuses}
                selected={selectedStatuses}
                onChange={setSelectedStatuses}
              />
            ) : undefined
          }
        />

        {report.shipmentManifests && <ShipmentManifestsSection data={report.shipmentManifests} />}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <ScanAgeOverview
            buckets={report.scanAgeBuckets}
            nonFieldItemCount={report.generalStatistics.nonFieldItems}
            tooltip={tooltips.scanAge}
          />
          <ScanAgeDistribution
            buckets={report.scanAgeBuckets}
            tooltip={tooltips.scanAgeDistribution}
          />
        </div>

        {report.siteSummary.length > 1 && (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <ItemsBySiteChart siteSummary={report.siteSummary} tooltip={tooltips.itemsBySite} />
            <SiteSummaryTable siteSummary={report.siteSummary} tooltip={tooltips.siteSummary} />
          </div>
        )}

        <TimeOnSiteSection data={report.timeOnSite} tooltip={tooltips.timeOnSite} />

        <RequiredStockLevelSection
          data={report.requiredStockLevel}
          tooltip={tooltips.requiredStockLevel}
        />

        <ImmobilizedStockSection
          data={report.immobilizedStock}
          tooltip={tooltips.immobilizedStock}
        />

        {report.clients && <ClientsSection data={report.clients} tooltip={tooltips.clients} />}

        <OverdueSection
          title={t("reports.inventoryScan.overdue.title0to7")}
          data={report.overdue0to7}
          noItemsMessage={t("reports.inventoryScan.overdue.noItems0to7")}
          csvFilenamePrefix="overdue-0-7-days"
          tooltip={tooltips.overdue0to7}
        />

        <OverdueSection
          title={t("reports.inventoryScan.overdue.title8to30")}
          data={report.overdue8to30}
          noItemsMessage={t("reports.inventoryScan.overdue.noItems8to30")}
          csvFilenamePrefix="overdue-8-30-days"
          tooltip={tooltips.overdue8to30}
        />

        <OverdueSection
          title={t("reports.inventoryScan.overdue.title31to90")}
          data={report.overdue31to90}
          noItemsMessage={t("reports.inventoryScan.overdue.noItems31to90")}
          csvFilenamePrefix="overdue-31-90-days"
          tooltip={tooltips.overdue31to90}
        />

        <OverdueSection
          title={t("reports.inventoryScan.overdue.title90plus")}
          data={report.overdue90plus}
          noItemsMessage={t("reports.inventoryScan.overdue.noItems90plus")}
          csvFilenamePrefix="overdue-90-plus-days"
          tooltip={tooltips.overdue90plus}
        />

        <NeverScannedSection data={report.neverScanned} tooltip={tooltips.neverScannedSection} />

        <KeyFindings findings={report.keyFindings} tooltip={tooltips.keyFindings} />

        <ReportFooter />
      </div>
    </div>
  );
};

export default ReportsPage;
